import { useState } from 'react';
import { DataCard } from '@/reports/index';
import { MaritalRecord } from '@/reports/types/people';
import { Accordion, styled, Text, DataList, Box } from '@/theme/index';
import { DateUtil, nameize, suffixToNumbers } from '@/utils/index';

interface MaritalRecordCardProps {
  record: MaritalRecord;
  thumbRating: () => JSX.Element;
  ownerName: string;
  isMarriage: boolean;
}

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginTop: `${theme.spacing(0)} !important`,
  '.MuiAccordionSummary-root': {
    flexDirection: 'row-reverse',
    padding: theme.spacing(0),
  },
  '.MuiAccordionSummary-content, .MuiAccordionSummary-content.Mui-expanded': {
    marginLeft: theme.spacing(1.25),
  },
  ':hover': {
    boxShadow: 'none',
  },
  border: 'none',
}));

export function MaritalRecordCard({
  record,
  thumbRating,
  ownerName,
  isMarriage,
}: MaritalRecordCardProps) {
  const dateFormater = new DateUtil();
  const [expanded, setExpanded] = useState(false);
  const [expandedPartner, setExpandedPartner] = useState(false);
  const title = isMarriage ? 'Marriage Records' : 'Divorce Records';
  const location = record.city
    ? `${record.city},`
    : record.county
    ? `${record.county},`
    : '';

  const RecordDetails = [
    [
      'Date',
      dateFormater.parseDateFromString(record.date?.full || '', 'MM/dd/yyyy') ??
        '',
    ],
    ['Ceremony', nameize(record.ceremony)],
    ['Filling Number', record.filing_number || ''],
    ['Children Involved', record.number_of_children || ''],
  ];

  const ownerDetails = [
    [
      'Prior Status',
      nameize(record.previous_marital_status?.toLocaleLowerCase() || ''),
    ],
    ['Count', `${suffixToNumbers(record.times_married)} Marriage`],
  ];

  const partnerDetails = [
    ['Age', `${record.partner?.age?.years || ''}`],
    ['Prior Status', nameize(record.partner?.previous_marital_status || '')],
    [
      'Count',
      `${suffixToNumbers(record.partner?.times_married ?? null)} Marriage`,
    ],
    ['Birthplace', record.partner?.birth_state ?? ''],
  ];

  if (!isMarriage) {
    RecordDetails.splice(1, 0, [
      'Grounds for Divorce',
      nameize(record.grounds),
    ]);
  }

  const bodyCard = (
    <Box marginTop={2.5}>
      <Text variant="h3" mb={1.25}>
        {nameize(record?.party_type)}
      </Text>
      {['grantor', 'grantee'].includes(
        record?.party_type?.toLocaleLowerCase() || ''
      ) ? (
        <Text variant="h2" color="primary">
          {nameize(ownerName)}
        </Text>
      ) : (
        <DataCard>
          <Text variant="h2" color="primary">
            {nameize(ownerName)}
          </Text>
          <StyledAccordion
            display={`Show ${
              expanded ? 'less' : 'more'
            } contact info about the ${record?.party_type?.toLocaleLowerCase()}`}
            square
            onChange={() => setExpanded(!expanded)}
          >
            <DataList items={ownerDetails} />
          </StyledAccordion>
        </DataCard>
      )}

      <Text variant="h3" mb={1.25}>
        {nameize(record?.partner?.party_type || '')}
      </Text>

      {['grantor', 'grantee'].includes(
        record?.partner?.party_type?.toLocaleLowerCase() || ''
      ) ? (
        <Text variant="h2" color="primary">
          {nameize(record.partner?.name?.full ?? null)}
        </Text>
      ) : (
        <DataCard>
          <Text variant="h2" color="primary">
            {nameize(record.partner?.name?.full ?? null)}
          </Text>
          <StyledAccordion
            display={`Show ${
              expandedPartner ? 'less' : 'more'
            } contact info about the ${record?.partner?.party_type?.toLocaleLowerCase()}`}
            square
            onChange={() => setExpandedPartner(!expandedPartner)}
          >
            <DataList items={partnerDetails} />
          </StyledAccordion>
        </DataCard>
      )}
    </Box>
  );
  return (
    <DataCard
      thumbRating={thumbRating}
      titleProps={{
        title: `${title} found in ${location} ${record.state}`,
      }}
      cardStyles={{ mb: 0 }}
      customBody={bodyCard}
    >
      <Text variant="h3">{isMarriage ? 'Marriage' : 'Divorce'} Details</Text>
      <DataList items={RecordDetails} />
    </DataCard>
  );
}
